import React, { useState, useCallback, RefObject, useEffect } from 'react';
import { PayloadAction } from '@reduxjs/toolkit';

import {
  HistoryResponseModel,
  HistoryResponseModelPagedResponseModel,
} from 'api/adminApiClient';
import HistoryList from './HistoryList';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { ActivitiesSectionPageTypeEnum } from '../enums';
import { getHistoryDataForCustomer } from 'redux/reducers/clientSlice';
import { getHistoryDataForRecruiter } from 'redux/reducers/recruiterSlice';
import { getHistoryDataForWorker } from 'redux/reducers/workerSlice';
import { customerDetailsUpdatedAtByAdminSelector } from 'redux/selectors/clientSelectors';
import { memberDetailsUpdatedAtByAdminSelector } from 'redux/selectors/recruiterSelectors';
import { workerDetailsUpdatedAtByAdminSelector } from 'redux/selectors/workerSelectors';
import { getHistoryDataForAssignment } from 'redux/reducers/assignmentSlice';
import { assignmentDetailsUpdatedAtByAdminSelector } from 'redux/selectors/assignmentSelectors';

type Props = {
  entityId: string;
  pageType: ActivitiesSectionPageTypeEnum;
  scrollRef: RefObject<HTMLElement>;
};

type HistoryDataType = HistoryResponseModel[];
type PagedHistoryDataType = HistoryResponseModelPagedResponseModel;

export default function HistorySection(props: Props) {
  const { entityId, pageType, scrollRef } = props;
  const dispatch = useAppDispatch();
  const memberDetailsUpdatedAtByAdmin = useAppSelector(
    memberDetailsUpdatedAtByAdminSelector,
  );
  const customerDetailsUpdatedAtByAdmin = useAppSelector(
    customerDetailsUpdatedAtByAdminSelector,
  );
  const workerDetailsUpdatedAtByAdmin = useAppSelector(
    workerDetailsUpdatedAtByAdminSelector,
  );
  const assignmentDetailsUpdatedAtByAdmin = useAppSelector(
    assignmentDetailsUpdatedAtByAdminSelector,
  );
  const [historyData, setHistoryData] = useState<HistoryDataType>([]);
  const [hasMore, setHasMore] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    initInfinityScroll();
  }, [
    entityId,
    memberDetailsUpdatedAtByAdmin,
    customerDetailsUpdatedAtByAdmin,
    workerDetailsUpdatedAtByAdmin,
    assignmentDetailsUpdatedAtByAdmin,
  ]);

  const initInfinityScroll = () => {
    setIsVisible(false);
    setHistoryData([]);
    setIsInitialDataLoaded(false);
    setHasMore(true);
    setTotalCount(0);
    const timer = setTimeout(() => {
      // to destroy InfiniteScroll
      setIsVisible(true);
    }, 0);

    return () => clearTimeout(timer);
  };

  const onSetHistoryData = useCallback(
    (result: PayloadAction<PagedHistoryDataType>, pageNumber: number) => {
      const loadedData = result.payload.details || [];
      const totalCount = result.payload.totalCount || 0;
      const actualData = pageNumber === 0 ? [] : historyData;
      const newHistoryData = [...actualData, ...loadedData];
      const hasMoreRows = newHistoryData.length < totalCount;

      setHasMore(hasMoreRows);
      setHistoryData(newHistoryData);
      setTotalCount(totalCount);
    },
    [historyData],
  );

  const getCorrectAction = useCallback(() => {
    switch (pageType) {
      case ActivitiesSectionPageTypeEnum.Customer:
        return getHistoryDataForCustomer;

      case ActivitiesSectionPageTypeEnum.Member:
        return getHistoryDataForRecruiter;

      case ActivitiesSectionPageTypeEnum.Assignment:
        return getHistoryDataForAssignment;

      case ActivitiesSectionPageTypeEnum.Worker:
      default:
        return getHistoryDataForWorker;
    }
  }, [pageType]);

  const onLoadMore = useCallback(
    async (pageNumber: number) => {
      const correctAction = getCorrectAction();

      const result = await dispatch(
        correctAction({
          entityId,
          pageNumber,
        }),
      );

      if (correctAction.fulfilled.match(result)) {
        onSetHistoryData(result, pageNumber);
      } else {
        setHasMore(false);
      }

      setIsInitialDataLoaded(true);
    },
    [getCorrectAction, dispatch, entityId, onSetHistoryData],
  );

  const getNoDataMessage = () => {
    switch (pageType) {
      case ActivitiesSectionPageTypeEnum.Customer:
        return "This customer doesn't have any changes history, yet.";
      case ActivitiesSectionPageTypeEnum.Member:
        return "This member doesn't have any changes history, yet.";
      case ActivitiesSectionPageTypeEnum.Assignment:
        return "This assignment doesn't have any changes history, yet.";
      case ActivitiesSectionPageTypeEnum.Worker:
      default:
        return "This worker doesn't have any changes history, yet.";
    }
  };

  return (
    <>
      {isInitialDataLoaded && (
        <>
          {!totalCount && (
            <span className="no-data-msg">{getNoDataMessage()}</span>
          )}
        </>
      )}

      {isVisible && (
        <HistoryList
          hasMore={hasMore}
          pageType={pageType}
          entityId={entityId}
          scrollRef={scrollRef}
          onLoadMore={onLoadMore}
          historyData={historyData}
        />
      )}
    </>
  );
}
